/**
 * Date formatting utilities for consistent date display across the application.
 */

/**
 * Format a Date as a YYYY-MM-DD string in local timezone.
 * Use this instead of toISOString() to avoid timezone bugs.
 */
export function formatDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Format a date as "Mon DD, YYYY" (e.g., "Jan 15, 2026")
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'No date'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return 'Invalid date'
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format a date as "Mon DD" (e.g., "Jan 15")
 */
export function formatShortDate(
  date: Date | string | null | undefined,
): string {
  if (!date) return 'No date'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return 'Invalid date'
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format a date with time as "Mon DD, YYYY, HH:MM AM/PM"
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return 'No date'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return 'Invalid date'
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format a date as relative time (e.g., "Just now", "5m ago", "2h ago", "3d ago")
 */
export function formatRelativeTime(
  date: Date | string | null | undefined,
): string {
  if (!date) return 'No date'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return 'Invalid date'

  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`

  return formatShortDate(d)
}

export interface DueDateInfo {
  text: string
  isOverdue: boolean
}

/**
 * Format a due date with contextual display (Today, Tomorrow, overdue, etc.)
 */
export function formatDueDate(
  date: Date | string | null | undefined,
): DueDateInfo | null {
  if (!date) return null

  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(d)
  dueDate.setHours(0, 0, 0, 0)

  const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / 86400000)

  if (diffDays < 0) {
    return { text: `${Math.abs(diffDays)}d overdue`, isOverdue: true }
  }
  if (diffDays === 0) {
    return { text: 'Due today', isOverdue: false }
  }
  if (diffDays === 1) {
    return { text: 'Due tomorrow', isOverdue: false }
  }
  if (diffDays <= 7) {
    return { text: `Due in ${diffDays}d`, isOverdue: false }
  }

  return {
    text: `Due ${formatShortDate(d)}`,
    isOverdue: false,
  }
}

/**
 * Get next birthday info for a person
 */
export function getNextBirthday(
  date: Date | string | null | undefined,
): string | null {
  if (!date) return null

  const today = new Date()
  const birthday = typeof date === 'string' ? new Date(date) : date
  if (isNaN(birthday.getTime())) return null
  const thisYearBirthday = new Date(
    today.getFullYear(),
    birthday.getMonth(),
    birthday.getDate(),
  )

  // If birthday already passed this year, check next year
  if (thisYearBirthday < today) {
    thisYearBirthday.setFullYear(today.getFullYear() + 1)
  }

  const diffDays = Math.ceil(
    (thisYearBirthday.getTime() - today.getTime()) / 86400000,
  )

  if (diffDays === 0) return 'Today!'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays <= 7) return `In ${diffDays} days`
  if (diffDays <= 30) return `In ${Math.ceil(diffDays / 7)} weeks`

  return null
}

/**
 * Format a birthday date as "Mon DD" (e.g., "Jan 15")
 */
export function formatBirthday(
  date: Date | string | null | undefined,
): string | null {
  if (!date) return null
  const formatted = formatShortDate(date)
  if (formatted === 'Invalid date') return null
  return formatted
}
